// src/services/notificationSync.ts
import { Capacitor } from "@capacitor/core";
import { supabase } from "../supabaseClient";
import {
  getPendingNotificationIds,
  notifyNow,
  scheduleReminder,
  stableNotificationId,
} from "./localNotifications";

const REMINDER_WINDOW_DAYS = 14;

export async function getMyActiveTeamIds(userId: string): Promise<string[]> {
  const [{ data: ownTeams }, { data: memberRows }] = await Promise.all([
    supabase
      .from("teams")
      .select("id")
      .eq("user_id", userId)
      .or("archived.is.null,archived.eq.false"),
    supabase
      .from("team_members")
      .select("team_id")
      .eq("user_id", userId)
      .eq("status", "active"),
  ]);

  const allTeamIds = [
    ...(ownTeams?.map((t: any) => t.id) ?? []),
    ...(memberRows?.map((m: any) => m.team_id) ?? []),
  ].filter(Boolean) as string[];

  if (allTeamIds.length === 0) return [];

  const { data: activeTeams } = await supabase
    .from("teams")
    .select("id")
    .in("id", Array.from(new Set(allTeamIds)))
    .or("archived.is.null,archived.eq.false");

  return (activeTeams?.map((t: any) => t.id) ?? []) as string[];
}

export async function getMyActiveClubIds(userId: string): Promise<string[]> {
  const { data } = await supabase
    .from("club_members")
    .select("club_id")
    .eq("user_id", userId)
    .eq("status", "active");
  return Array.from(new Set((data?.map((m: any) => m.club_id) ?? []) as string[]));
}

/**
 * Programme des rappels locaux pour les matchs/entraînements à venir des équipes
 * de l'utilisateur. Idempotent : ne reprogramme pas un rappel déjà en attente.
 */
export async function syncUpcomingEventReminders(teamIds: string[], reminderMinutes: number) {
  // Rappels programmés = notifications natives uniquement (cf. localNotifications.ts).
  // Inutile d'interroger Supabase pour ça depuis le navigateur.
  if (!Capacitor.isNativePlatform()) return;
  if (teamIds.length === 0) return;

  const nowISO = new Date().toISOString();
  const windowEnd = new Date(Date.now() + REMINDER_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const [{ data: matches }, { data: trainings }, { data: teams }, pendingIds] = await Promise.all([
    supabase
      .from("matches")
      .select("id, opponent, date, team_id")
      .in("team_id", teamIds)
      .gte("date", nowISO)
      .lte("date", windowEnd),
    supabase
      .from("trainings")
      .select("id, date, notes, team_id")
      .in("team_id", teamIds)
      .gte("date", nowISO)
      .lte("date", windowEnd),
    supabase.from("teams").select("id, team_name").in("id", teamIds),
    getPendingNotificationIds(),
  ]);

  const teamNames = new Map((teams ?? []).map((t: any) => [t.id, t.team_name as string]));

  for (const m of matches ?? []) {
    const notifId = stableNotificationId(`match-${m.id}`);
    if (pendingIds.has(notifId)) continue;
    const at = new Date(new Date(m.date).getTime() - reminderMinutes * 60 * 1000);
    const teamName = teamNames.get(m.team_id) || "Ton équipe";
    await scheduleReminder(
      notifId,
      "Match à venir",
      `${teamName} vs ${m.opponent || "?"} — bientôt !`,
      at,
      { type: "match", id: m.id, teamId: m.team_id }
    );
  }

  for (const tr of trainings ?? []) {
    const notifId = stableNotificationId(`training-${tr.id}`);
    if (pendingIds.has(notifId)) continue;
    const at = new Date(new Date(tr.date).getTime() - reminderMinutes * 60 * 1000);
    const teamName = teamNames.get(tr.team_id) || "Ton équipe";
    await scheduleReminder(
      notifId,
      "Entraînement à venir",
      `${teamName} — entraînement bientôt !`,
      at,
      { type: "training", id: tr.id, teamId: tr.team_id }
    );
  }
}

export function subscribeTeamChatNotifications(
  teamIds: string[],
  currentUserId: string
): () => void {
  if (teamIds.length === 0) return () => {};
  const teamIdSet = new Set(teamIds);

  const channel = supabase
    .channel("notif-team-messages")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "team_messages" },
      (payload) => {
        const row = payload.new as any;
        if (!row || row.user_id === currentUserId) return;
        if (!teamIdSet.has(row.team_id)) return;
        void notifyNow(
          stableNotificationId(`chat-${row.id}`),
          row.sender_name || "Nouveau message",
          row.content || "",
          { type: "chat", teamId: row.team_id }
        );
      }
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}

export function subscribeClubPostsNotifications(
  teamIds: string[],
  clubIds: string[],
  currentUserId: string
): () => void {
  if (teamIds.length === 0 && clubIds.length === 0) return () => {};
  const teamIdSet = new Set(teamIds);
  const clubIdSet = new Set(clubIds);

  const channel = supabase
    .channel("notif-club-posts")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "team_posts" },
      (payload) => {
        const row = payload.new as any;
        if (!row || row.author_id === currentUserId) return;
        if (!teamIdSet.has(row.team_id)) return;
        void notifyNow(
          stableNotificationId(`teampost-${row.id}`),
          row.title || row.author_name || "Nouvelle publication",
          row.content || "",
          { type: "team_post", teamId: row.team_id }
        );
      }
    )
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "posts" },
      (payload) => {
        const row = payload.new as any;
        if (!row || row.author_user_id === currentUserId) return;
        if (!row.club_id || !clubIdSet.has(row.club_id)) return;
        void notifyNow(
          stableNotificationId(`clubpost-${row.id}`),
          row.title || "Nouvelle publication du club",
          row.body || "",
          { type: "club_post", clubId: row.club_id }
        );
      }
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
